import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FilterOption } from '@/types/filters.types';

interface FilterSelectProps {
  value: string[];
  options: FilterOption[];
  allLabel?: string;
  onChange: (v: string[]) => void;
}

export function FilterSelect({
  value,
  options,
  allLabel = 'All',
  onChange,
}: FilterSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus search when open
  useEffect(() => {
    if (open && options.length > 6) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open, options.length]);

  const filtered = useMemo(() => {
    const base = options.filter((o) => !/----/.test(o.label));
    if (!search.trim()) return base;
    const term = search.toLowerCase();
    return base.filter((o) => o.label.toLowerCase().includes(term));
  }, [options, search]);

  const selected = new Set(value);
  const isSearching = search.trim().length > 0;

  /**
   * With a search term active, "Select All" applies only to the matching
   * options and merges into the existing selection, so picks the search is
   * hiding are left alone.
   */
  function selectAll() {
    if (isSearching) {
      onChange([...new Set([...value, ...filtered.map((o) => o.value)])]);
      return;
    }
    onChange(options.map((o) => o.value));
  }

  function toggle(optValue: string) {
    if (selected.has(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  }

  const buttonLabel =
    value.length === 0
      ? allLabel
      : value.length === 1
        ? options.find((o) => o.value === value[0])?.label ?? '1 selected'
        : `${value.length} selected`;

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full flex items-center gap-2 text-left',
          'px-2.5 py-1.5 text-xs rounded border border-slate-200 bg-white',
          'hover:border-slate-300 transition-colors cursor-pointer',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          value.length > 0 && 'border-blue-300 bg-blue-50/50',
        )}
      >
        <span className={cn('flex-1 truncate', value.length === 0 && 'text-slate-400')}>
          {buttonLabel}
        </span>
        {value.length > 0 && (
          <span
            className="flex-shrink-0 text-slate-400 hover:text-slate-600"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
          >
            <X className="h-3 w-3" />
          </span>
        )}
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-slate-400 transition-transform duration-150 flex-shrink-0',
            open && 'rotate-180',
          )}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[12rem] rounded-md bg-white border border-slate-200 shadow-lg">
          {/* Search (show if > 6 options) */}
          {options.length > 6 && (
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Options */}
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-slate-400 text-center">No options found</p>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggle(opt.value)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left',
                    'hover:bg-slate-50 transition-colors',
                    selected.has(opt.value) && 'bg-blue-50/50',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(opt.value)}
                    readOnly
                    tabIndex={-1}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 cursor-pointer flex-shrink-0 pointer-events-none"
                  />
                  <span className="truncate text-slate-700">{opt.label}</span>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100">
            <button
              type="button"
              onClick={selectAll}
              disabled={isSearching && filtered.length === 0}
              title={
                isSearching
                  ? `Add the ${filtered.length} matching option${filtered.length === 1 ? '' : 's'} to the selection`
                  : 'Select every option'
              }
              className="text-[11px] font-medium text-blue-600 hover:text-blue-700 transition-colors disabled:cursor-not-allowed disabled:text-slate-300"
            >
              {isSearching ? `Select ${filtered.length}` : 'Select All'}
            </button>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[11px] font-medium text-slate-500 hover:text-slate-700 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
